/**
 * Route resolution for sync manifest entries.
 *
 * Does NOT import from workshop-routes.tsx to avoid circular dependency
 * (workshop-routes.tsx imports SyncStatusPage). Instead, derives routes
 * from file path conventions:
 *   - Pages: workshop/pages/<path>-page.tsx → /workshop/<path>
 *   - Support files: fallback to first route in same directory
 *   - Components: map to showcase page by directory
 *
 * Files that don't match any convention get a Component Preview link
 * so they can still be rendered standalone in the browser.
 */

/** Maps component directories to their showcase page route */
export const COMPONENT_SHOWCASE_ROUTES: Record<string, string> = {
  "components/canvas": "/workshop/canvas",
  "components/cursors": "/workshop/canvas",
  "components/panels": "/workshop/panels",
  "components/layout": "/workshop/full-app",
  "components/settings": "/workshop/settings",
  "components/shapes": "/workshop/shapes",
};

/** Specific shape overrides for frames that have dedicated showcase pages */
export const SHAPE_SHOWCASE_ROUTES: Record<string, string> = {
  "workflow-frame": "/workshop/shapes/workflow",
  "memory-frame": "/workshop/shapes/memory",
};

/**
 * Fallback routes for page support files (layouts, buttons, etc.)
 * that aren't directly routable. Maps directory prefixes to the first
 * relevant workshop page route.
 */
export const PAGE_DIR_FALLBACKS: Record<string, string> = {
  "auth/": "/workshop/auth/login",
  "onboarding/": "/workshop/onboarding",
  "shapes/": "/workshop/shapes",
  "canvas/": "/workshop/canvas",
  "panels/": "/workshop/panels",
  "journeys/": "/workshop/journeys/canvas-session",
  "export/": "/workshop/export",
};

export function resolveRoute(workshopPath: string): string {
  if (/^workshop\/(components\/panels\/composer-(redesign|model-menu)\.tsx|pages\/panels\/composer-redesign\.css)$/.test(workshopPath)) {
    return "/workshop/panels/composer-redesign";
  }

  // Pages: workshop/pages/auth/login-page.tsx → /workshop/auth/login
  if (workshopPath.startsWith("workshop/pages/")) {
    const stripped = workshopPath
      .replace("workshop/pages/", "")
      .replace(/\.tsx$/, "");

    // Direct match: strip -page suffix → route path
    const asRoute = stripped.replace(/-page$/, "");
    if (
      asRoute.startsWith("auth/") ||
      asRoute.startsWith("journeys/") ||
      asRoute.startsWith("shapes/") ||
      asRoute.startsWith("canvas/") ||
      asRoute.startsWith("panels/") ||
      ["settings", "full-app", "onboarding", "glass-lab", "sync-status", "preview", "design-system/recipes"].includes(asRoute)
    ) {
      return `/workshop/${asRoute}`;
    }

    // Support files — find fallback by directory prefix
    for (const [prefix, route] of Object.entries(PAGE_DIR_FALLBACKS)) {
      if (stripped.startsWith(prefix)) return route;
    }
  }

  // Components: workshop/components/shapes/memory-frame/memory-frame.tsx
  if (workshopPath.startsWith("workshop/components/")) {
    const stripped = workshopPath.replace("workshop/", "");

    // Check shape-specific overrides first
    for (const [frameDir, route] of Object.entries(SHAPE_SHOWCASE_ROUTES)) {
      if (stripped.includes(`shapes/${frameDir}/`)) return route;
    }

    // Fall back to directory-based showcase mapping
    for (const [dir, route] of Object.entries(COMPONENT_SHOWCASE_ROUTES)) {
      if (stripped.startsWith(dir)) return route;
    }
  }

  // Fallback: open in Component Preview page
  return `/workshop/preview?file=${encodeURIComponent(workshopPath)}`;
}
